import Phaser from "phaser";
import {
  NineSliceButton,
  NineSlicePanel,
  NINE_SLICE_BUTTON_EVENT,
} from "@yjsgames/phaser-ui";
import {
  CONTENT_PANEL_STYLE,
  FONT_FAMILY,
  SMALL_BUTTON_STYLE,
  TYPO,
} from "./uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import {
  DIALOGUE_PORTRAIT_CAPTAIN_KEY,
  DIALOGUE_PORTRAIT_COMMAND_KEY,
  DIALOGUE_PORTRAIT_OPERATION_LOG_KEY,
} from "../themes/imageKeys.js";
import { getTextDirection, t } from "../i18n/i18n.js";
import { DEPTH } from "../objects/depthLayers.js";
import {
  resolveBattleDialogueOverlayLayout,
  type DialogueOverlayRect,
} from "./battleDialogueOverlayLayout.js";
import {
  createTypewriterState,
  startTypewriter,
  revealNextChar,
  completeTypewriter as completeTypewriterState,
  stopTypewriter,
  type TypewriterState,
} from "./battleDialoguePlayback.js";
import {
  createSignalMotionState,
  syncSignalMotion,
  syncEndMarker,
  syncPulse,
  type SignalMotionState,
  type SignalMotionTarget,
  type TweenHolder,
} from "./battleDialogueFx.js";

export type { BattleDialogueLineView, BattleDialogueData } from "./battleDialogueTypes.js";
import type { BattleDialogueData } from "./battleDialogueTypes.js";

const BUTTON_W = 72;
const BUTTON_H = 18;
const PANEL_H = 96;
const PANEL_DEPTH = DEPTH.dialoguePanel;
const PORTRAIT_HEAD_R = 13;
const SCANLINE_ALPHA = 0.32;
const ACTION_BUTTON_W = 28;
const ACTION_BUTTON_H = 18;
const CLOSE_BUTTON_SIZE = 18;
const HOLO_ALPHA = 0.82;

function setVisibleActive<
  T extends Phaser.GameObjects.GameObject & {
    setVisible: (visible: boolean) => T;
  }
>(obj: T, visible: boolean): void {
  obj.setVisible(visible);
  (
    obj as Phaser.GameObjects.GameObject & {
      setActive?: (value: boolean) => unknown;
    }
  ).setActive?.(visible);
}

function resolveSpeakerPortrait(speaker: string): {
  readonly color: number;
  readonly initial: string;
  readonly textureKey: string;
} {
  if (speaker.includes("함장") || speaker.toLowerCase().includes("captain")) {
    return {
      color: PALETTE_NUM.infoBlue,
      initial: speaker.includes("함장") ? "함" : "C",
      textureKey: DIALOGUE_PORTRAIT_CAPTAIN_KEY,
    };
  }
  if (speaker.includes("미확인") || speaker.toLowerCase().includes("unknown")) {
    return {
      color: PALETTE_NUM.signalDanger,
      initial: "?",
      textureKey: DIALOGUE_PORTRAIT_OPERATION_LOG_KEY,
    };
  }
  if (speaker.includes("작전") || speaker.toLowerCase().includes("log")) {
    return {
      color: PALETTE_NUM.accentGold,
      initial: speaker.includes("작전") ? "기" : "L",
      textureKey: DIALOGUE_PORTRAIT_OPERATION_LOG_KEY,
    };
  }
  return {
    color: PALETTE_NUM.accentGold,
    initial: speaker.includes("지구") ? "지" : "E",
    textureKey: DIALOGUE_PORTRAIT_COMMAND_KEY,
  };
}

export class BattleDialogueOverlay {
  private readonly commsButton: NineSliceButton;
  private readonly commsGlyphCore: Phaser.GameObjects.Arc;
  private readonly commsGlyphStem: Phaser.GameObjects.Rectangle;
  private readonly commsButtonWaves: readonly Phaser.GameObjects.Arc[];
  private readonly ping: Phaser.GameObjects.Arc;
  private readonly panelBg: NineSlicePanel;
  private readonly headerBar: Phaser.GameObjects.Rectangle;
  private readonly panelLeds: readonly Phaser.GameObjects.Arc[];
  private readonly projectionBeams: readonly Phaser.GameObjects.Rectangle[];
  private readonly scanlines: readonly Phaser.GameObjects.Rectangle[];
  private readonly cornerRails: readonly Phaser.GameObjects.Rectangle[];
  private readonly portraitBg: Phaser.GameObjects.Rectangle;
  private readonly portraitSignal: Phaser.GameObjects.Rectangle;
  private readonly portraitRing: Phaser.GameObjects.Arc;
  private readonly portraitWaveBars: readonly Phaser.GameObjects.Rectangle[];
  private readonly signalWaves: readonly Phaser.GameObjects.Arc[];
  private readonly portraitImage: Phaser.GameObjects.Image;
  private readonly portraitHead: Phaser.GameObjects.Arc;
  private readonly portraitInitialText: Phaser.GameObjects.Text;
  private readonly speakerText: Phaser.GameObjects.Text;
  private readonly bodyText: Phaser.GameObjects.Text;
  private readonly previousButton: NineSliceButton;
  private readonly nextButton: NineSliceButton;
  private readonly endMarkerGlow: Phaser.GameObjects.Arc;
  private readonly closeButton: NineSliceButton;
  private readonly tw: TypewriterState;
  private readonly signalState: SignalMotionState;
  private readonly endMarkerHolder: TweenHolder = { tween: undefined };
  private readonly pulseHolder: TweenHolder = { tween: undefined };
  private visible = true;
  private current: BattleDialogueData | null = null;
  private open = false;
  private lineIndex = 0;
  private portraitImageAvailable = false;

  constructor(
    private readonly scene: Phaser.Scene,
    rect: DialogueOverlayRect,
    private readonly onComplete: (dialogueId: string) => void
  ) {
    this.tw = createTypewriterState();
    this.signalState = createSignalMotionState();

    this.commsButton = new NineSliceButton(scene, 0, 0, {
      style: SMALL_BUTTON_STYLE,
      label: t("dialogue.comms"),
      widthOverride: BUTTON_W,
      heightOverride: BUTTON_H,
    });
    this.commsButton.setDepth(PANEL_DEPTH);
    this.commsButton.setBgAlpha(0.68);
    this.commsButton.setBgTint(PALETTE_NUM.infoBlue);
    this.commsButton.on(NINE_SLICE_BUTTON_EVENT.Click, () => this.openPanel());
    scene.add.existing(this.commsButton);

    this.commsGlyphCore = scene.add
      .circle(0, 0, 4, PALETTE_NUM.infoBlue, 0.88)
      .setStrokeStyle(1, PALETTE_NUM.textBright, 0.92)
      .setDepth(DEPTH.dialogueContent)
      .setVisible(false);
    this.commsGlyphStem = scene.add
      .rectangle(0, 0, 2, 5, PALETTE_NUM.infoBlue, 0.95)
      .setOrigin(0, 0)
      .setDepth(DEPTH.dialogueContent)
      .setVisible(false);
    this.commsButtonWaves = [7, 11].map((radius) =>
      scene.add
        .arc(0, 0, radius, -35, 35, false, PALETTE_NUM.infoBlue, 0)
        .setStrokeStyle(1, PALETTE_NUM.infoBlue, radius === 7 ? 0.86 : 0.52)
        .setDepth(DEPTH.dialogueContent)
        .setVisible(false)
    );

    this.ping = scene.add
      .circle(0, 0, 4, PALETTE_NUM.signalDanger, 1)
      .setDepth(DEPTH.dialogueContent);

    this.panelBg = new NineSlicePanel(scene, 0, 0, {
      style: CONTENT_PANEL_STYLE,
      width: 1,
      height: PANEL_H,
    });
    this.panelBg.setDepth(PANEL_DEPTH).setAlpha(0.76).setVisible(false);
    scene.add.existing(this.panelBg);

    this.headerBar = scene.add
      .rectangle(0, 0, 1, 1, PALETTE_NUM.infoBlue, 0.22)
      .setOrigin(0, 0)
      .setDepth(DEPTH.dialogueContent)
      .setVisible(false);
    this.panelLeds = [0, 1, 2].map((i) =>
      scene.add
        .circle(
          0,
          0,
          i === 1 ? 2.4 : 1.8,
          PALETTE_NUM.infoBlue,
          i === 1 ? 0.95 : 0.64
        )
        .setStrokeStyle(1, PALETTE_NUM.textBright, 0.24)
        .setDepth(DEPTH.dialogueContent)
        .setVisible(false)
    );
    this.projectionBeams = Array.from({ length: 3 }, () =>
      scene.add
        .rectangle(0, 0, 1, 1, PALETTE_NUM.infoBlue, 0.24)
        .setOrigin(0, 0.5)
        .setDepth(DEPTH.dialogueContent)
        .setVisible(false)
    );
    this.scanlines = Array.from({ length: 4 }, () =>
      scene.add
        .rectangle(0, 0, 1, 1, PALETTE_NUM.infoBlue, SCANLINE_ALPHA)
        .setOrigin(0, 0)
        .setDepth(DEPTH.dialogueContent)
        .setVisible(false)
    );
    this.cornerRails = Array.from({ length: 4 }, () =>
      scene.add
        .rectangle(0, 0, 1, 1, PALETTE_NUM.infoBlue, 0.86)
        .setOrigin(0, 0)
        .setDepth(DEPTH.dialogueContent)
        .setVisible(false)
    );

    this.portraitBg = scene.add
      .rectangle(0, 0, 1, 1, PALETTE_NUM.infoBlue, 0.22)
      .setOrigin(0, 0)
      .setStrokeStyle(1, PALETTE_NUM.infoBlue, 0.82)
      .setDepth(DEPTH.dialogueContent)
      .setVisible(false);

    this.portraitSignal = scene.add
      .rectangle(0, 0, 3, 1, PALETTE_NUM.infoBlue, 0.95)
      .setOrigin(0, 0)
      .setDepth(DEPTH.dialogueContent)
      .setVisible(false);

    this.portraitRing = scene.add
      .circle(0, 0, 1, PALETTE_NUM.infoBlue, 0)
      .setStrokeStyle(1, PALETTE_NUM.infoBlue, 0.88)
      .setDepth(DEPTH.dialogueContent)
      .setVisible(false);
    this.portraitWaveBars = Array.from({ length: 3 }, () =>
      scene.add
        .rectangle(0, 0, 1, 1, PALETTE_NUM.accentGold, 0.72)
        .setOrigin(0, 0)
        .setDepth(DEPTH.dialogueContent)
        .setVisible(false)
    );
    this.signalWaves = [6, 11, 16].map((radius) =>
      scene.add
        .arc(0, 0, radius, -42, 42, false, PALETTE_NUM.infoBlue, 0)
        .setStrokeStyle(1, PALETTE_NUM.infoBlue, radius === 6 ? 0.72 : 0.42)
        .setDepth(DEPTH.dialogueContent)
        .setVisible(false)
    );

    this.portraitImage = scene.add
      .image(0, 0, DIALOGUE_PORTRAIT_OPERATION_LOG_KEY)
      .setDepth(DEPTH.dialogueContent + 0.1)
      .setAlpha(0.9)
      .setVisible(false);

    this.portraitHead = scene.add
      .circle(0, 0, PORTRAIT_HEAD_R, PALETTE_NUM.infoBlue, 0.66)
      .setDepth(DEPTH.dialogueContent)
      .setVisible(false);

    const rtl = getTextDirection() === "rtl";
    this.portraitInitialText = scene.add
      .text(0, 0, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.label,
        color: PALETTE.textBright,
        align: "center",
      })
      .setOrigin(0.5, 0.5)
      .setDepth(DEPTH.dialogueContent)
      .setVisible(false);

    this.speakerText = scene.add
      .text(0, 0, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.accentGold,
        align: rtl ? "right" : "left",
      })
      .setOrigin(0, 0)
      .setDepth(DEPTH.dialogueContent)
      .setVisible(false);

    this.bodyText = scene.add
      .text(0, 0, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.body,
        color: PALETTE.textPrimary,
        align: rtl ? "right" : "left",
        wordWrap: { width: Math.max(120, rect.width - 20) },
      })
      .setOrigin(0, 0)
      .setDepth(DEPTH.dialogueContent)
      .setVisible(false);

    this.previousButton = new NineSliceButton(scene, 0, 0, {
      style: SMALL_BUTTON_STYLE,
      label: "<",
      widthOverride: ACTION_BUTTON_W,
      heightOverride: ACTION_BUTTON_H,
    });
    this.previousButton.setDepth(DEPTH.dialogueContent);
    this.previousButton.setBgAlpha(0.58);
    this.previousButton.setBgTint(PALETTE_NUM.infoBlue);
    this.previousButton.setVisible(false);
    this.previousButton.on(NINE_SLICE_BUTTON_EVENT.Click, () => this.goBack());
    scene.add.existing(this.previousButton);

    this.nextButton = new NineSliceButton(scene, 0, 0, {
      style: SMALL_BUTTON_STYLE,
      label: ">",
      widthOverride: ACTION_BUTTON_W,
      heightOverride: ACTION_BUTTON_H,
    });
    this.nextButton.setDepth(DEPTH.dialogueContent);
    this.nextButton.setBgAlpha(0.66);
    this.nextButton.setBgTint(PALETTE_NUM.infoBlue);
    this.nextButton.setVisible(false);
    this.nextButton.on(NINE_SLICE_BUTTON_EVENT.Click, () => this.advance());
    scene.add.existing(this.nextButton);

    this.endMarkerGlow = scene.add
      .circle(0, 0, 12, PALETTE_NUM.infoBlue, 0.18)
      .setStrokeStyle(1, PALETTE_NUM.textBright, 0.44)
      .setDepth(DEPTH.dialogueContent - 0.2)
      .setVisible(false);

    this.closeButton = new NineSliceButton(scene, 0, 0, {
      style: SMALL_BUTTON_STYLE,
      label: "X",
      widthOverride: CLOSE_BUTTON_SIZE,
      heightOverride: CLOSE_BUTTON_SIZE,
    });
    this.closeButton.setDepth(DEPTH.dialogueContent);
    this.closeButton.setBgAlpha(0.78);
    this.closeButton.setBgTint(PALETTE_NUM.signalDanger);
    this.closeButton.setVisible(false);
    this.closeButton.on(NINE_SLICE_BUTTON_EVENT.Click, () => this.closePanel());
    scene.add.existing(this.closeButton);

    this.resize(rect);
    this.update(null);
  }

  resize(rect: DialogueOverlayRect): void {
    const layout = resolveBattleDialogueOverlayLayout(rect);
    this.commsButton.setPosition(layout.button.x, layout.button.y);
    this.commsGlyphCore.setPosition(layout.buttonIcon.x, layout.buttonIcon.y);
    this.commsGlyphStem
      .setPosition(layout.buttonStem.x, layout.buttonStem.y)
      .setSize(layout.buttonStem.width, layout.buttonStem.height);
    this.commsButtonWaves.forEach((wave, i) => {
      const waveLayout = layout.buttonWaves[i];
      if (!waveLayout) return;
      wave
        .setPosition(waveLayout.cx, waveLayout.cy)
        .setRadius(waveLayout.radius);
    });
    this.ping.setPosition(layout.ping.x, layout.ping.y);

    this.panelBg
      .setPosition(layout.panel.x, layout.panel.y)
      .setSize(layout.panel.width, layout.panel.height);
    this.headerBar
      .setPosition(layout.headerBar.x, layout.headerBar.y)
      .setSize(layout.headerBar.width, layout.headerBar.height);
    this.panelLeds.forEach((led, i) => {
      const ledLayout = layout.leds[i];
      if (!ledLayout) return;
      led.setPosition(ledLayout.x, ledLayout.y);
    });
    this.projectionBeams.forEach((beam, i) => {
      const beamLayout = layout.projectionBeams[i];
      if (!beamLayout) return;
      beam
        .setPosition(beamLayout.x, beamLayout.y)
        .setSize(beamLayout.width, beamLayout.height)
        .setRotation(beamLayout.rotation);
    });
    this.scanlines.forEach((line, i) => {
      const lineLayout = layout.scanlines[i];
      if (!lineLayout) return;
      line
        .setPosition(lineLayout.x, lineLayout.y)
        .setSize(lineLayout.width, lineLayout.height);
    });
    layout.cornerBrackets.forEach((bracket, i) => {
      const horizontal = this.cornerRails[i * 2];
      const vertical = this.cornerRails[i * 2 + 1];
      const horizontalX = bracket.flipX ? bracket.x - bracket.size : bracket.x;
      const horizontalY = bracket.flipY ? bracket.y - 1 : bracket.y;
      const verticalX = bracket.flipX ? bracket.x - 1 : bracket.x;
      const verticalY = bracket.flipY ? bracket.y - bracket.size : bracket.y;
      horizontal
        ?.setPosition(horizontalX, horizontalY)
        .setSize(bracket.size, 2);
      vertical?.setPosition(verticalX, verticalY).setSize(2, bracket.size);
    });
    this.portraitBg
      .setPosition(layout.portrait.x, layout.portrait.y)
      .setSize(layout.portrait.width, layout.portrait.height);
    this.portraitSignal
      .setPosition(layout.portrait.x, layout.portrait.y)
      .setSize(3, layout.portrait.height);
    this.portraitRing
      .setPosition(layout.portraitRing.x, layout.portraitRing.y)
      .setRadius(layout.portraitRing.radius);
    this.portraitWaveBars.forEach((bar, i) => {
      const barLayout = layout.portraitWaveBars[i];
      if (!barLayout) return;
      bar
        .setPosition(barLayout.x, barLayout.y)
        .setSize(barLayout.width, barLayout.height);
    });
    this.signalWaves.forEach((wave, i) => {
      const waveLayout = layout.signalWaves[i];
      if (!waveLayout) return;
      wave
        .setPosition(waveLayout.cx, waveLayout.cy)
        .setRadius(waveLayout.radius);
    });
    this.portraitHead.setPosition(
      layout.portraitInitial.x,
      layout.portraitInitial.y
    );
    this.portraitImage
      .setPosition(layout.portraitInitial.x, layout.portraitInitial.y)
      .setDisplaySize(
        Math.max(1, layout.portrait.width - 6),
        Math.max(1, layout.portrait.height - 6)
      );
    this.portraitInitialText.setPosition(
      layout.portraitInitial.x,
      layout.portraitInitial.y
    );
    this.speakerText.setPosition(layout.speaker.x, layout.speaker.y);
    this.bodyText
      .setPosition(layout.body.x, layout.body.y)
      .setWordWrapWidth(layout.body.width, true)
      .setFixedSize(layout.body.width, layout.body.height);
    this.previousButton.setPosition(
      layout.previousButton.x,
      layout.previousButton.y
    );
    this.nextButton.setPosition(layout.nextButton.x, layout.nextButton.y);
    this.endMarkerGlow.setPosition(layout.nextButton.x, layout.nextButton.y);
    this.closeButton.setPosition(layout.closeButton.x, layout.closeButton.y);
    this.syncVisibility();
  }

  update(data: BattleDialogueData | null): void {
    const idChanged = this.current?.dialogueId !== data?.dialogueId;
    this.current = data;
    if (idChanged) {
      this.open = false;
      this.lineIndex = 0;
      this.tw.renderedLineIndex = -1;
      this.tw.renderedLineText = "";
      stopTypewriter(this.tw);
    }
    if (!data) {
      this.open = false;
      stopTypewriter(this.tw);
    }
    if (this.open) this.renderLine();
    this.syncVisibility();
  }

  play(data: BattleDialogueData): void {
    this.update(data);
    this.openPanel();
  }

  applyLanguage(): void {
    this.commsButton.setLabel(t("dialogue.comms"));
    this.previousButton.setLabel("<");
    this.nextButton.setLabel(this.isLastLine() ? "." : ">");
    this.closeButton.setLabel("X");
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.syncVisibility();
  }

  private openPanel(): void {
    if (!this.current || this.current.lines.length === 0) return;
    this.open = true;
    this.lineIndex = 0;
    this.renderLine();
    this.syncVisibility();
  }

  private advance(): void {
    const data = this.current;
    if (!data) return;
    const result = completeTypewriterState(this.tw);
    if (result.completed) {
      this.bodyText.setText(result.text);
      this.syncVisibility();
      return;
    }
    if (!this.isLastLine()) {
      this.lineIndex += 1;
      this.renderLine();
      this.syncVisibility();
      return;
    }
    this.closePanel();
  }

  private goBack(): void {
    if (this.lineIndex <= 0) return;
    this.lineIndex -= 1;
    this.renderLine();
    this.syncVisibility();
  }

  private closePanel(): void {
    const data = this.current;
    if (!data) return;
    stopTypewriter(this.tw);
    this.onComplete(data.dialogueId);
    this.open = false;
    this.current = null;
    this.syncVisibility();
  }

  private renderLine(): void {
    const line = this.current?.lines[this.lineIndex];
    if (!line) return;
    if (
      this.tw.renderedLineIndex === this.lineIndex &&
      this.tw.renderedLineText === line.text
    )
      return;
    this.tw.renderedLineIndex = this.lineIndex;
    this.tw.renderedLineText = line.text;
    const portrait = resolveSpeakerPortrait(line.speaker);
    const hologramColor =
      portrait.color === PALETTE_NUM.signalDanger
        ? PALETTE_NUM.signalDanger
        : PALETTE_NUM.infoBlue;
    this.portraitSignal.setFillStyle(hologramColor, HOLO_ALPHA);
    this.portraitRing.setStrokeStyle(1, hologramColor, 0.88);
    this.portraitWaveBars.forEach((bar, i) =>
      bar.setFillStyle(hologramColor, i === 0 ? 0.78 : 0.48)
    );
    this.signalWaves.forEach((wave, i) =>
      wave.setStrokeStyle(1, hologramColor, i === 0 ? 0.72 : 0.42)
    );
    this.portraitHead.setFillStyle(hologramColor, 0.66);
    this.portraitInitialText.setText(portrait.initial);
    this.portraitImageAvailable = this.scene.textures.exists(portrait.textureKey);
    if (this.portraitImageAvailable) {
      this.portraitImage.setTexture(portrait.textureKey).clearTint();
    }
    this.speakerText.setText(line.speaker);
    this.bodyText.setText("");
    startTypewriter(this.tw, line.text, this.scene, () =>
      this.onTypewriterTick()
    );
    this.nextButton.setLabel(this.isLastLine() ? "." : ">");
  }

  private onTypewriterTick(): void {
    const result = revealNextChar(this.tw);
    this.bodyText.setText(result.text);
    if (result.done) this.syncVisibility();
  }

  private isLastLine(): boolean {
    return !this.current || this.lineIndex >= this.current.lines.length - 1;
  }

  private syncVisibility(): void {
    const buttonVisible = this.visible && !!this.current && !this.open;
    this.commsButton.setVisible(buttonVisible);
    setVisibleActive(this.commsGlyphCore, buttonVisible);
    setVisibleActive(this.commsGlyphStem, buttonVisible);
    this.commsButtonWaves.forEach((wave) =>
      setVisibleActive(wave, buttonVisible)
    );
    this.ping.setVisible(buttonVisible && this.current?.unread === true);
    const panelVisible = this.visible && this.open && !!this.current;
    setVisibleActive(this.panelBg, panelVisible);
    setVisibleActive(this.headerBar, panelVisible);
    this.panelLeds.forEach((led) => setVisibleActive(led, panelVisible));
    this.projectionBeams.forEach((beam) =>
      setVisibleActive(beam, panelVisible)
    );
    this.scanlines.forEach((line) => setVisibleActive(line, panelVisible));
    this.cornerRails.forEach((rail) => setVisibleActive(rail, panelVisible));
    setVisibleActive(this.portraitBg, panelVisible);
    setVisibleActive(this.portraitSignal, panelVisible);
    setVisibleActive(this.portraitRing, panelVisible);
    this.portraitWaveBars.forEach((bar) => setVisibleActive(bar, panelVisible));
    this.signalWaves.forEach((wave) => setVisibleActive(wave, panelVisible));
    setVisibleActive(
      this.portraitImage,
      panelVisible && this.portraitImageAvailable
    );
    setVisibleActive(
      this.portraitHead,
      panelVisible && !this.portraitImageAvailable
    );
    setVisibleActive(
      this.portraitInitialText,
      panelVisible && !this.portraitImageAvailable
    );
    setVisibleActive(this.speakerText, panelVisible);
    setVisibleActive(this.bodyText, panelVisible);
    this.previousButton.setVisible(panelVisible && this.lineIndex > 0);
    this.nextButton.setVisible(panelVisible);
    this.closeButton.setVisible(panelVisible);

    const signalTargets: SignalMotionTarget[] = [
      this.portraitSignal,
      this.portraitRing,
      ...this.portraitWaveBars,
      ...this.signalWaves,
    ];
    syncSignalMotion(
      this.signalState,
      this.scene,
      signalTargets,
      panelVisible,
      this.tw.active,
    );
    syncEndMarker(
      this.endMarkerHolder,
      this.scene,
      this.endMarkerGlow,
      panelVisible && this.isLastLine() && !this.tw.active,
      setVisibleActive,
    );
    syncPulse(
      this.pulseHolder,
      this.scene,
      this.ping,
      buttonVisible && this.current?.unread === true,
    );
  }
}
